/**
 * Transformers for converting between database format (snake_case) and app format (camelCase)
 */

// ============================================
// TASK TRANSFORMERS
// ============================================

/**
 * Transform task from database format to app format
 */
export function transformTaskFromDB(dbTask) {
  if (!dbTask) return null;

  return {
    id: dbTask.id,
    projectId: dbTask.project_id,
    title: dbTask.title,
    description: dbTask.description,
    priority: dbTask.priority,
    dueDate: dbTask.due_date,
    endDate: dbTask.end_date,
    startTime: dbTask.start_time,
    duration: dbTask.duration,
    isMultiDay: dbTask.is_multi_day,
    completed: dbTask.completed,
    completedAt: dbTask.completed_at,
    pointsAwarded: dbTask.points_awarded,
    pointsDeducted: dbTask.points_deducted,
    pointsValue: dbTask.points_value,
    flexMode: dbTask.flex_mode,
    antiOverlap: dbTask.anti_overlap,
    archived: dbTask.archived || false,
    archivedAt: dbTask.archived_at,
    createdAt: dbTask.created_at,
    updatedAt: dbTask.updated_at,
    // Transform subtasks if present
    subTasks: dbTask.subtasks ? dbTask.subtasks.filter(st => !st.deleted_at).map(transformSubtaskFromDB) : [],
    // Transform tags if present (from task_tags junction)
    tags: dbTask.task_tags ? dbTask.task_tags.map(tt => tt.tag_id) : []
  };
}

/**
 * Transform task from app format to database format
 */
export function transformTaskToDB(appTask) {
  return {
    project_id: appTask.projectId,
    title: appTask.title,
    description: appTask.description || null,
    priority: appTask.priority || 'medium',
    due_date: appTask.dueDate || null,
    end_date: appTask.endDate || null,
    start_time: appTask.startTime || null,
    duration: appTask.duration || null,
    is_multi_day: appTask.isMultiDay || false,
    completed: appTask.completed || false,
    completed_at: appTask.completedAt || null,
    points_awarded: appTask.pointsAwarded || false,
    points_deducted: appTask.pointsDeducted || false,
    points_value: appTask.pointsValue || 10,
    flex_mode: appTask.flexMode || false,
    anti_overlap: appTask.antiOverlap || false,
    archived: appTask.archived || false,
    archived_at: appTask.archivedAt || null
  };
}

// ============================================
// SUBTASK TRANSFORMERS
// ============================================

/**
 * Transform subtask from database format to app format
 */
export function transformSubtaskFromDB(dbSubtask) {
  if (!dbSubtask) return null;

  return {
    id: dbSubtask.id,
    taskId: dbSubtask.task_id,
    title: dbSubtask.title,
    dueDate: dbSubtask.due_date,
    endDate: dbSubtask.end_date,
    time: dbSubtask.time,
    duration: dbSubtask.duration,
    completed: dbSubtask.completed,
    completedAt: dbSubtask.completed_at,
    isMultiDay: dbSubtask.is_multi_day,
    position: dbSubtask.position,
    createdAt: dbSubtask.created_at,
    updatedAt: dbSubtask.updated_at
  };
}

/**
 * Transform subtask from app format to database format
 */
export function transformSubtaskToDB(appSubtask) {
  return {
    task_id: appSubtask.taskId,
    title: appSubtask.title,
    due_date: appSubtask.dueDate || null,
    end_date: appSubtask.endDate || null,
    time: appSubtask.time || null,
    duration: appSubtask.duration || null,
    completed: appSubtask.completed || false,
    completed_at: appSubtask.completedAt || null,
    is_multi_day: appSubtask.isMultiDay || false,
    position: appSubtask.position || 0
  };
}

// ============================================
// PROJECT TRANSFORMERS
// ============================================

/**
 * Transform project from database format to app format
 */
export function transformProjectFromDB(dbProject) {
  if (!dbProject) return null;

  return {
    id: dbProject.id,
    ownerId: dbProject.owner_id,
    name: dbProject.name,
    color: dbProject.color,
    isDefault: dbProject.is_default,
    createdAt: dbProject.created_at,
    updatedAt: dbProject.updated_at
  };
}

/**
 * Transform project from app format to database format
 */
export function transformProjectToDB(appProject) {
  return {
    owner_id: appProject.ownerId,
    name: appProject.name,
    color: appProject.color || '#3b82f6',
    is_default: appProject.isDefault || false
  };
}

// ============================================
// TAG TRANSFORMERS
// ============================================

/**
 * Transform tag from database format to app format
 */
export function transformTagFromDB(dbTag) {
  if (!dbTag) return null;

  return {
    id: dbTag.id,
    userId: dbTag.user_id,
    name: dbTag.name,
    color: dbTag.color,
    createdAt: dbTag.created_at,
    updatedAt: dbTag.updated_at
  };
}

/**
 * Transform tag from app format to database format
 */
export function transformTagToDB(appTag) {
  return {
    user_id: appTag.userId,
    name: appTag.name,
    color: appTag.color || '#64748b' // Default slate-500 color
  };
}

// ============================================
// PROFILE TRANSFORMERS
// ============================================

/**
 * Transform profile from database format to app format
 */
export function transformProfileFromDB(dbProfile) {
  if (!dbProfile) return null;

  return {
    id: dbProfile.id,
    email: dbProfile.email,
    darkMode: dbProfile.dark_mode,
    completionSoundEnabled: dbProfile.completion_sound_enabled,
    language: dbProfile.language,
    autoArchiveEnabled: dbProfile.auto_archive_enabled,
    autoArchiveDays: dbProfile.auto_archive_days,
    createdAt: dbProfile.created_at,
    updatedAt: dbProfile.updated_at
  };
}

/**
 * Transform profile from app format to database format
 */
export function transformProfileToDB(appProfile) {
  const dbProfile = {};

  if (appProfile.darkMode !== undefined) dbProfile.dark_mode = appProfile.darkMode;
  if (appProfile.completionSoundEnabled !== undefined) dbProfile.completion_sound_enabled = appProfile.completionSoundEnabled;
  if (appProfile.language !== undefined) dbProfile.language = appProfile.language;
  if (appProfile.autoArchiveEnabled !== undefined) dbProfile.auto_archive_enabled = appProfile.autoArchiveEnabled;
  if (appProfile.autoArchiveDays !== undefined) dbProfile.auto_archive_days = appProfile.autoArchiveDays;

  return dbProfile;
}
